import React from 'react';
import { Play } from 'lucide-react';

function StitchDetailModal({ stitch, onClose, colors, fonts, patterns, onPatternClick }) {
  const difficultyColor = stitch.difficulty === "Beginner Friendly" ? "#9ec5d4" :
                         stitch.difficulty === "Beginner" ? colors.sageGreen :
                         stitch.difficulty === "Intermediate" ? colors.mustard : colors.dustyRust;

  // Patterns that use this stitch (published only)
  const usedInPatterns = patterns
    ? patterns.filter(p => p.status === 'published' && Array.isArray(p.stitches) && p.stitches.includes(stitch.id))
    : [];

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: "2rem"
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: "20px",
          maxWidth: "800px",
          width: "100%",
          maxHeight: "90vh",
          overflow: "auto",
          position: "relative"
        }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "1.5rem",
            right: "1.5rem",
            background: "rgba(255,255,255,0.95)",
            border: "none",
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            fontSize: "1.5rem",
            color: colors.charcoal,
            zIndex: 1,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
          }}
        >
          ×
        </button>

        {/* Hero Image or Abbreviation Fallback */}
        <div style={{
          width: "100%",
          height: "240px",
          overflow: "hidden",
          borderRadius: "20px 20px 0 0",
          position: "relative",
          background: `linear-gradient(135deg, ${colors.sageGreen}22, ${colors.mustard}22)`
        }}>
          {stitch.image ? (
            <img
              src={stitch.image}
              alt={stitch.name}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover"
              }}
              onError={(e) => {
                e.target.style.display = "none";
                // Show abbreviation fallback on image error
                e.target.nextSibling.style.display = "flex";
              }}
            />
          ) : null}
          {/* Abbreviation fallback — shown when no image or image fails */}
          <div
            style={{
              display: stitch.image ? "none" : "flex",
              position: "absolute",
              inset: 0,
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: "0.5rem"
            }}
          >
            <div style={{
              fontFamily: fonts.display,
              fontSize: "4.5rem",
              color: colors.sageGreen,
              lineHeight: 1,
              textShadow: "0 2px 12px rgba(0,0,0,0.08)"
            }}>
              {stitch.abbreviation}
            </div>
            <div style={{
              fontSize: "0.9rem",
              color: colors.brownGray,
              fontWeight: "600",
              letterSpacing: "0.05em",
              textTransform: "uppercase"
            }}>
              {stitch.name}
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: "2.5rem" }}>
          {/* Header */}
          <div style={{ marginBottom: "2rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "0.75rem" }}>
              <h2 style={{
                fontSize: "2rem",
                fontWeight: "700",
                color: colors.charcoal,
                margin: 0
              }}>
                {stitch.abbreviation} - {stitch.name}
              </h2>
              <div style={{
                padding: "0.5rem 1rem",
                background: difficultyColor,
                color: "#fff",
                borderRadius: "50px",
                fontSize: "0.9rem",
                fontWeight: "600",
                flexShrink: 0
              }}>
                {stitch.difficulty}
              </div>
            </div>
            <p style={{
              fontSize: "1.1rem",
              color: colors.darkGray,
              lineHeight: "1.6",
              margin: 0
            }}>
              {stitch.description}
            </p>
          </div>

          {/* Instructions */}
          {stitch.instructions && stitch.instructions.length > 0 && (
            <div style={{ marginBottom: "2rem" }}>
              <h3 style={{
                fontSize: "1.3rem",
                fontWeight: "700",
                color: colors.charcoal,
                marginBottom: "1rem"
              }}>
                How to Make It
              </h3>
              <ol style={{
                paddingLeft: "1.5rem",
                margin: 0
              }}>
                {stitch.instructions.map((instruction, idx) => (
                  <li
                    key={idx}
                    style={{
                      fontSize: "1rem",
                      color: colors.darkGray,
                      lineHeight: "1.8",
                      marginBottom: "0.5rem"
                    }}
                  >
                    {instruction}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Tutorial Link */}
          <div style={{ marginBottom: usedInPatterns.length > 0 ? "2rem" : 0 }}>
            {stitch.tutorialUrl ? (
              <a
                href={stitch.tutorialUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  padding: "1rem 2rem",
                  background: colors.sageGreen,
                  color: "#fff",
                  borderRadius: "12px",
                  textDecoration: "none",
                  fontSize: "1.1rem",
                  fontWeight: "600",
                  transition: "all 0.2s"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.2)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <Play size={20} />
                Watch Video Tutorial
              </a>
            ) : (
              <div style={{
                padding: "1rem",
                background: colors.bgCream,
                borderRadius: "12px",
                color: colors.brownGray,
                fontSize: "0.95rem"
              }}>
                Video tutorial coming soon! Use the written instructions above to get started.
              </div>
            )}
          </div>

          {/* Used in These Patterns */}
          {usedInPatterns.length > 0 && (
            <div>
              <h3 style={{
                fontSize: "1.1rem",
                fontWeight: "700",
                color: colors.charcoal,
                marginBottom: "1rem"
              }}>
                Patterns Using This Stitch
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {usedInPatterns.map(p => (
                  <div
                    key={p.id}
                    onClick={() => { onClose(); onPatternClick && onPatternClick(p); }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      padding: "0.75rem 1rem",
                      background: colors.bgCream,
                      borderRadius: "10px",
                      cursor: "pointer",
                      color: colors.charcoal,
                      transition: "background 0.15s"
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = colors.borderGreige}
                    onMouseLeave={(e) => e.currentTarget.style.background = colors.bgCream}
                  >
                    {p.image && (
                      <img
                        src={p.image}
                        alt={p.title}
                        style={{ width: "44px", height: "44px", objectFit: "cover", borderRadius: "8px", flexShrink: 0 }}
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    )}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: "600", fontSize: "0.95rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {p.title}
                      </div>
                      {p.siteName && (
                        <div style={{ fontSize: "0.8rem", color: colors.darkGray }}>{p.siteName}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default StitchDetailModal;
